/*
 * Copyright (c) 2025.
 * This code and its derivatives belong to BETICA LTD and may not be copied, reproduced, amended or used in any way without the permission of BETICA LTD
 * @author torrejonv
 *
 */

/**
 * @file securityQuestionSlice.js
 * @description Redux slice for managing securityQuestion related state and operations
 */

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import createAxiosInstance from "../async/axios";
import { getRequestParams } from "../async/get-fetch-params";

/**
 * @function getInitialState
 * @description Creates the initial state object for the securityQuestion slice
 * @param {Object} params - Initial state parameters
 * @returns {Object} The initial state object
 */
export const getInitialState = (questions = null) => ({
  error: null,
  loading: false,
  questions,
});

/**
 * @function getSecurityQuestion
 * @description Async thunk for get security question operations
 * @param {Object} data - Parameters for the operation
 * @returns {Promise<Object>} Result of the operation
 */
export const getSecurityQuestion = createAsyncThunk("questions/loadQuestions", async (data, thunkAPI) => {
  try {
    const { authToken, language, lineId, originId } = getRequestParams(thunkAPI.getState());

    const axios = createAxiosInstance(thunkAPI.dispatch, {
      authToken,
      language,
    });

    const result = await axios.get(`/player/securityquestions?originId=${originId}&lineId=${lineId}`);

    return {
      questions: result.data,
    };
  } catch (err) {
    const customError = {
      message: err.response?.headers["x-information"] || "Unable to obtain question details", // serializable (err.response.data)
      name: "Questions List Fetch Error",
      status: err.response?.statusText,
    };
    throw customError;
  }
});

/**
 * @constant securityQuestionSlice
 * @description Redux slice for securityQuestion management
 */
const securityQuestionSlice = createSlice({
  extraReducers: (builder) => {
    builder
      .addCase(getSecurityQuestion.pending, (state) => {
        state.error = null;
        state.loading = true;
        state.questions = null;
      })
      .addCase(getSecurityQuestion.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
      })
      .addCase(getSecurityQuestion.fulfilled, (state, action) => {
        state.error = null;
        state.loading = false;
        state.questions = action.payload.questions;
      });
  },
  initialState: getInitialState(),
  name: "question",
  reducers: {},
});
const { reducer } = securityQuestionSlice;
export default reducer;
